import HyperDX from '@hyperdx/browser';

const API_KEY = process.env.REACT_APP_HYPERDX_API_KEY;
const SERVICE = process.env.REACT_APP_HYPERDX_SERVICE || 'kleep-bodyscan-webapp';
// Self-hosted ingestion endpoint (e.g. the kleep.ai HyperDX collector).
// REQUIRED to enable telemetry: when unset, initTelemetry() is a no-op. We
// deliberately never fall back to the SDK's cloud default (in-otel.hyperdx.io).
// The value is supplied per-environment via env, never hardcoded.
const HDX_URL = process.env.REACT_APP_HYPERDX_URL;

const escapeForRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Never trace these:
// - S3 presigned URLs embed auth signatures
// - data: URLs would embed the whole base64 body-scan photo in the span
// - the collector itself: the SDK only self-excludes its cloud default, so a
//   custom endpoint gets instrumented and every export would emit a new span,
//   which gets exported, which emits a span... (self-trace loop)
const IGNORE_URLS = [
  /amazonaws\.com/i,
  /^data:/i,
  /in-otel\.hyperdx\.io/i,
  ...(HDX_URL ? [new RegExp(escapeForRegex(HDX_URL), 'i')] : []),
];

let initialized = false;
const lastSentAt = {};

// Derives the deployment environment so dev and prod telemetry stay separable
// in HyperDX (filter on the `environment` attribute). NODE_ENV is the reliable
// signal: CRA sets it to 'development' under `npm start` (including LAN-IP phone
// testing, where the hostname is a bare IP) and 'production' under `npm run
// build` — so a dev build can never be mistagged as production.
export const getEnvironment = () => {
  if (process.env.NODE_ENV !== 'production') return 'development';
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('staging') || host.includes('dev')) return 'staging';
  return 'production';
};

export const initTelemetry = () => {
  // Both the ingestion key AND a self-hosted URL are required. Without a URL we
  // stay off rather than shipping to the HyperDX cloud default.
  if (initialized || !API_KEY || !HDX_URL) return;
  try {
    HyperDX.init({
      apiKey: API_KEY,
      service: SERVICE,
      tracePropagationTargets: [/kleep\.ai/i],
      ignoreUrls: IGNORE_URLS,
      // OFF on purpose: this flow logs large TF.js model objects (GraphModel).
      // Serializing them on every console.log crashed mobile Safari (memory).
      // Telemetry comes from the explicit trackScanEvent() calls instead.
      consoleCapture: false,
      // Body-scan photos transit through fetch: never capture request/response bodies
      advancedNetworkCapture: false,
      maskAllInputs: true,
      // No screen recording: this flow shows the user's body through the camera
      disableReplay: true,
      // Route to the self-hosted instance; the SDK derives the OTLP endpoints
      // from this base. HDX_URL is guaranteed set (guarded in initTelemetry).
      url: HDX_URL,
      tracesUrl: `${HDX_URL}/v1/traces`,
      logsUrl: `${HDX_URL}/v1/logs`,
    });
    initialized = true;
    // Session-wide tags so every event/error can be filtered by environment.
    // Set right after init; scan-specific context (mid, gender…) is layered on
    // later via setScanContext without clearing these.
    HyperDX.setGlobalAttributes({
      environment: getEnvironment(),
      host: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
    });
  } catch (e) {
    console.warn('HyperDX init failed:', e);
  }
};

// Attributes attached to every subsequent event of the session (mid, gender, ...)
export const setScanContext = (attributes) => {
  if (!initialized) return;
  try {
    HyperDX.setGlobalAttributes(attributes);
  } catch (e) {
    console.warn('HyperDX setGlobalAttributes failed:', e);
  }
};

// throttleMs guards events that can fire once per animation frame
export const trackScanEvent = (name, metadata = {}, throttleMs = 0) => {
  if (!initialized) return;
  try {
    if (throttleMs > 0) {
      const now = Date.now();
      if (lastSentAt[name] && now - lastSentAt[name] < throttleMs) return;
      lastSentAt[name] = now;
    }
    HyperDX.addAction(name, metadata);
  } catch (e) {
    console.warn('HyperDX addAction failed:', e);
  }
};
